import type { Translations } from "./ru";

export const en: Translations = {
  common: {
    save: "Save",
    saved: "Saved",
    saving: "Saving…",
    cancel: "Cancel",
    delete: "Delete",
    add: "Add",
    back: "Back",
    close: "Close",
    open: "Open",
    retry: "Retry",
    loading: "Loading…",
    yes: "Yes",
    no: "No",
    error: "Error",
    copy: "Copy",
    copied: "Copied",
    search: "Search",
    edit: "Edit",
    create: "Create",
    done: "Done",
    from: "from",
    unsaved: "You have unsaved changes",
  },
  nav: {
    sites: "Sites",
    subscriptions: "Plans",
    settings: "Profile",
    admin: "Admin",
  },
  auth: {
    connecting: "Connecting…",
    failed: "Sign-in failed",
    outsideTelegram: "Opened outside Telegram, so the app runs in demo mode with local data.",
    mockBadge: "Demo mode",
  },
  sites: {
    title: "My sites",
    hasChanges: "Unpublished edits",
    empty: "No sites yet",
    emptyHint: "Create your first — the first 7 days are free",
    create: "New site",
    chooseTemplate: "Choose a template",
    namePlaceholder: "Site name",
    nameHint: "Visible only to you, in the sites list",
    open: "Open",
    deleteConfirm: "Delete site “{{title}}”? This cannot be undone.",
    limitReached: "You reached the sites limit of your plan",
    paidOnce: "One-time fee",
    upgrade: "Choose a plan",
    status: {
      draft: "Draft",
      publishing: "Publishing",
      published: "Published",
      publish_error: "Publish error",
      expired: "Unpublished",
    },
  },
  editor: {
    title: "Builder",
    blocks: "Blocks",
    design: "Design",
    addBlock: "Add block",
    blockSettings: "Block settings",
    noBlocks: "No blocks yet — add the first one",
    hide: "Hide",
    show: "Show",
    up: "Up",
    down: "Down",
    undo: "Undo",
    redo: "Redo",
    preview: "Preview",
    publish: "Publish",
    update: "Update",
    updateSite: "Update site",
    unpublishedChanges: "The site is published, but these edits are not live yet. Update it when you are done.",
    siteTitle: "Site name",
    pageTitle: "Page title",
    pageDescription: "Description for search and previews",
    theme: "Theme",
    accent: "Accent color",
    backgroundPhoto: "Background photo",
    backgroundPhotoHint: "Upload a photo — it covers the whole page",
    removePhoto: "Remove photo",
    backgroundDim: "Photo dimming",
    backgroundDimHint: "The stronger it is, the better text reads over the photo",
    background: "Custom background (CSS)",
    backgroundHint: "For example: linear-gradient(160deg,#fff,#eee)",
    items: "Items",
    addItem: "Add “{{label}}”",
    itemNumber: "{{label}} {{number}}",
    imageHint: "Paste an image URL",
    uploadImage: "Upload photo",
    saveFailed: "Could not save: {{reason}}",
    showPreview: "Show preview",
    hidePreview: "Hide preview",
    blockEmpty: "block is still empty, open it and fill it in",
    siteMissing: "This site was deleted — there is nothing to save into. Open the list and create a new one.",
    backToSites: "Back to sites",
  },
  preview: {
    title: "Preview",
    mobile: "Phone",
    desktop: "Desktop",
    refresh: "Refresh",
    serverRender: "Server render",
    localRender: "Instant render",
    hint: "This is what visitors will see",
    emptySite: "Add blocks in the builder — your site will appear here",
    emptyCustomCode: "Write some HTML — your site will appear here",
  },
  customCode: {
    title: "Custom code",
    locked: "Custom code is not paid yet",
    description:
      "The whole page is built from your own HTML, CSS and JavaScript — no builder blocks here.",
    html: "HTML",
    css: "CSS",
    js: "JavaScript",
    save: "Save code",
    paid: "Paid",
    priceHint: "A one-time fee for one site — no plan needed. It never expires: if you delete the site, it moves to your next custom code site.",
    priceMissing: "Price is not configured — contact the administrator",
    buy: "Pay {{price}} Gram",
    purchased: "Paid — custom code is unlocked for this site",
    recovered: "Already paid — custom code is unlocked, no need to pay again",
    checking: "Checking payment…",
    checkingHint: "If you have already paid, access opens by itself as soon as the transaction reaches the network — usually within a minute. No need to pay again.",
    payFirst: "Pay for custom code first",
    uploadImage: "Upload photo",
    uploadHint: "The link is inserted straight into the current tab",
    imageInserted: "Photo uploaded, link inserted into the code",
    warning: "The code goes onto the page as is. Check it — a JS error breaks the whole page.",
  },
  domain: {
    title: "Domain",
    subtitle: "Get a subdomain in the platform zone so your site opens by address",
    zoneSuffix: "zone",
    zoneNotReady: "The subdomain zone is not configured by the admin yet",
    modeSubdomain: "Platform subdomain",
    modeOwn: "My own .ton domain",
    ownLabel: "Your domain",
    ownHint: "The domain must belong to the connected wallet",
    attachOwn: "Attach domain",
    placeholder: "myname",
    check: "Check",
    available: "Domain is free",
    taken: "Domain is taken",
    invalid: "3–125 chars: latin letters, digits and hyphen",
    mode: "Zone type",
    modeProxy: "Proxy — regular subdomain",
    modeSbt: "SBT — non-transferable",
    deploy: "Sign and attach",
    walletRequired: "Connect your wallet first",
    connectWallet: "Connect wallet",
    waiting: "Waiting for the transaction on-chain…",
    pending: "Payment went through. The domain is still being issued — tap Get again in a minute, you will not be charged twice",
    attached: "Domain attached",
    current: "Current domain",
  },
  publish: {
    title: "Publishing",
    publishing: "Publishing your site…",
    publishingHint: "Rendering the page and uploading it to TON Storage. Usually under a minute.",
    published: "Site is published",
    publishedAt: "Published {{date}}",
    error: "Publishing failed",
    retry: "Try again",
    update: "Update site",
    updateHint: "The domain and link stay the same and nothing needs signing — visitors see the new version right away.",
    updated: "Site updated",
    hasChanges: "There are edits that are not on the published site yet. Update it so visitors can see them.",
    stuck: "Publishing takes longer than usual. The job seems lost — try again.",
    bagId: "TON Storage bag id",
    bindDns: "Bind to domain",
    bindHint: "Sign a transaction so the domain serves the new version",
    domainReady: "The domain points at your site — it opens in a TON browser",
    bindSite: "Enable direct delivery",
    bindSiteHint: "The domain will open straight from our server, bypassing public gateways",
    bound: "Domain updated",
    openSite: "Open site",
    openHint: "A plain link that works in any browser. The .ton domain opens in a TON browser once bound.",
    trialNotice: "Your first site is published free for 7 days. After that a plan is required.",
    subscriptionRequired: "An active plan is required to publish",
  },
  tariffs: {
    title: "Plans",
    subtitle: "Paid in Gram from your connected wallet",
    sitesLimit_one: "{{count}} site",
    sitesLimit_few: "{{count}} sites",
    sitesLimit_many: "{{count}} sites",
    duration: {
      month: "month",
      "3month": "3 months",
      "6month": "6 months",
      "12month": "year",
      forever: "forever",
    },
    durationShort: {
      month: "1 mo",
      "3month": "3 mo",
      "6month": "6 mo",
      "12month": "12 mo",
      forever: "Forever",
    },
    per: {
      month: "per month",
      "3month": "for 3 months",
      "6month": "for 6 months",
      "12month": "per year",
      forever: "one-time payment",
    },
    noOption: "not available",
    priceNote: "The longer the term, the cheaper the month. “Forever” is a one-time payment with no renewals.",
    buy: "Subscribe",
    paying: "Waiting for the wallet signature…",
    confirming: "Verifying the payment on-chain…",
    success: "Subscription activated",
    recovered: "Your earlier payment was found on-chain and credited: {{names}}. No need to pay again",
    empty: "The admin has not configured plans yet",
  },
  subscriptions: {
    title: "My plans",
    empty: "No active plans",
    trial: "Trial",
    manual: "Granted by admin",
    until: "until {{date}}",
    forever: "forever",
    renew: "Renew",
    status: {
      active: "Active",
      expiring_soon: "Expiring soon",
      expired: "Expired",
      manual: "Granted manually",
    },
    expiringNotice: "Plan expires on {{date}} — renew to keep your site online",
    expiredNotice: "Plan expired, your sites are unpublished",
  },
  settings: {
    title: "Profile",
    wallet: "TON wallet",
    walletHint: "Needed for payments and domain ownership",
    notConnected: "Not connected",
    proofFailed: "Wallet connected, but ownership could not be verified. Try again or reconnect the wallet.",
    language: "Language",
    theme: "Theme",
    themeLight: "Light",
    themeDark: "Dark",
    about: "About the author",
    version: "Version",
    resetDemo: "Reset demo data",
  },
  about: {
    title: "About the author",
    hint: "Who built the app and how to get in touch",
    open: "Open",
    failed: "Could not load the author page",
  },
  admin: {
    title: "Admin",
    tabs: {
      stats: "Stats",
      users: "Users",
      tariffs: "Plans",
      domains: "Domains",
      zone: "Zone",
      about: "Author",
    },
    about: {
      hint: "Users see this text and link on the «About the author» page.",
      name: "Heading",
      text: "Text",
      linkUrl: "Link",
      linkHint: "Telegram profile, website or email — leave empty to hide the button",
      linkLabel: "Button label",
    },
    stats: {
      users: "Users",
      sites: "Sites",
      published: "Published",
      subscriptions: "Active plans",
      revenue: "Revenue, Gram",
      revenue30: "Last 30 days, Gram",
      payments: "Payments",
    },
    users: {
      search: "Search by username, id or wallet",
      empty: "Nobody found",
      sites: "sites",
      subs: "plans",
      grant: "Grant access",
      revoke: "Revoke",
      granted: "Access granted",
      revoked: "Access revoked",
      wallet: "Wallet",
      registered: "Registered",
      payments: "Payments",
      noPayments: "No payments",
    },
    grant: {
      title: "Grant access",
      tariff: "Plan",
      manual: "Custom limit",
      sitesLimit: "Sites limit",
      duration: "Duration",
      comment: "Comment",
      submit: "Grant",
    },
    tariffs: {
      oneTime: "one-time",
      create: "New plan",
      name: "Name",
      description: "Description",
      price: "Price, Gram",
      sitesLimit: "Sites limit",
      duration: "Duration",
      kind: "Kind",
      active: "Active",
      inactive: "Hidden",
      deleteConfirm: "Delete plan “{{name}}”?",
      saved: "Plan saved",
    },
    domains: { empty: "No domains yet", owner: "Owner" },
    zone: {
      hint: "The platform owns one .ton domain and deploys a subdomain zone on it once. After that users get addresses like name.yourdomain.ton.",
      status: "Zone status",
      ready: "Ready",
      notReady: "Not configured",
      domain: "Platform domain",
      domainHint: "For example mysites.ton — must be owned by the admin wallet",
      dnsItem: "Domain DNS item address",
      dnsItemHint: "On-chain NFT address of this domain",
      mode: "How subdomains are issued",
      modeHint: "Fixed once by the deploy transaction and cannot be changed later",
      modeSbt: "Mint instantly",
      modeProxy: "Via auction",
      modeLocked: "The zone is already deployed — the type can no longer be changed. A different type needs a new zone on another domain.",
      collection: "Subdomain collection address",
      collectionHint: "Appears after the zone is deployed — paste it here",
      deploy: "Deploy zone",
      deployHint: "One-time operation, signed by the domain owner",
      deployed: "Deploy transaction sent",
    },
  },
  errors: {
    LIMIT_EXCEEDED: "You reached the sites limit of your plan",
    SUBSCRIPTION_REQUIRED: "An active plan is required",
    NOT_A_CUSTOM_CODE_SITE: "Custom code is only available in a project of that type",
    WALLET_NOT_CONNECTED: "Connect your TON wallet",
    PAYMENT_NOT_CONFIRMED: "Payment is not on-chain yet. Wait a moment and retry",
    TX_ALREADY_USED: "This transaction is already used",
    DOMAIN_TAKEN: "Domain is already taken",
    PUBLISH_IN_PROGRESS: "The site is already being published — wait a few seconds",
    ZONE_NOT_DEPLOYED: "The collection is not deployed yet — sign the zone deploy transaction first",
    NETWORK_ERROR: "No connection to the server",
    INIT_DATA_INVALID: "Telegram authorization error",
  },
};
